import { ReportSection, PatientInfoField } from "../../../services/mdtReportStructure.service";

export interface PreviewBranding {
  logoUrl?: string | null;
  hospitalName?: string | null;
  headerText?: string | null;
  addressSnapshot?: string | null;
  contactSnapshot?: string | null;
  signatureLeftLabel?: string | null;
  signatureRightLabel?: string | null;
  footerText?: string | null;
}

export interface PreviewStructure {
  reportTitle?: string;
  reportIdPrefix?: string;
  sections?: ReportSection[];
  patientInfoFields?: PatientInfoField[];
}

interface MdtReportPreviewProps {
  branding: PreviewBranding;
  structure: PreviewStructure;
}

const SAMPLE_PATIENT_VALUES: Record<string, string> = {
  name: "[Patient name]",
  meetingDate: "[Meeting date]",
  dob: "[Date of birth]",
  nhs: "[NHS number]",
  department: "[Department]",
  chair: "[MDT chair]",
  referrer: "[Referring clinician]",
};

const TABLE_COLUMNS = [
  { label: "Selected", badge: "bg-green-50 text-green-700 border border-green-100" },
  { label: "Alternative", badge: "bg-amber-50 text-amber-700 border border-amber-100" },
  { label: "Rejected", badge: "bg-gray-50 text-gray-500 border border-gray-100" },
];

const numberedSectionTitle = (section: ReportSection, position: number) => {
  const raw = (section.displayTitle || "").trim();
  const stripped = raw.replace(/^\s*\d+[.)-]\s*/, "").trim();
  return `${position + 1}. ${stripped || "Untitled section"}`;
};

const MdtReportPreview = ({ branding, structure }: MdtReportPreviewProps) => {
  const sections = (structure.sections || []).filter((s) => s.enabled);

  return (
    <div className="max-w-3xl mx-auto">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {/* Header block: logo / name / address header */}
        <div className="bg-neutral-50 border-b border-gray-200 px-8 py-6">
          <div className="flex items-start gap-4">
            <div className="h-16 w-16 rounded-lg border border-gray-200 bg-white shrink-0 flex items-center justify-center overflow-hidden">
              {branding.logoUrl ? (
                <img src={branding.logoUrl} alt="Hospital logo" className="h-full w-full object-contain" />
              ) : (
                <span className="px-1 text-center text-[10px] leading-tight text-gray-400">Hospital logo</span>
              )}
            </div>
            <div className="min-w-0">
              <p className="text-lg font-bold text-gray-900 leading-snug">
                {branding.hospitalName || "Hospital name"}
              </p>
              {branding.headerText && (
                <p className="text-sm font-medium text-primary-700 mt-0.5">{branding.headerText}</p>
              )}
              {branding.addressSnapshot && (
                <p className="text-sm text-gray-500 mt-0.5 whitespace-pre-line">{branding.addressSnapshot}</p>
              )}
              {branding.contactSnapshot && (
                <p className="text-sm text-gray-500 mt-0.5">{branding.contactSnapshot}</p>
              )}
            </div>
          </div>
        </div>

        <div className="px-8 py-6 space-y-7">
          {/* Report title and metadata */}
          <div>
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:gap-0">
              <h2 className="text-2xl font-bold text-gray-900">
                {structure.reportTitle || "MULTIDISCIPLINARY TEAM CLINICAL REPORT"}
              </h2>
              <div className="flex shrink-0 flex-col items-stretch gap-1 text-left text-sm text-gray-600 sm:ml-auto sm:w-max">
                {structure.reportIdPrefix && <span>{structure.reportIdPrefix}001234</span>}
                <span>
                  Date:{" "}
                  {new Date().toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  })}
                </span>
                <span className="inline-flex w-max items-center gap-1.5 rounded-full bg-green-50 border border-green-200 px-2 py-0.5 text-xs font-medium text-green-700">
                  <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
                  Status: Approved
                </span>
              </div>
            </div>
            <div className="mt-1.5 h-1 w-full bg-primary-600 rounded-full" />
          </div>

          {/* Patient / meeting fields (live, only those enabled; sample values) */}
          <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50/60 px-4 py-3">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
              Patient &amp; meeting information
            </p>
            <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-4">
              {(structure.patientInfoFields || [])
                .filter((f) => f.enabled)
                .map((f) => (
                  <div key={f.key}>
                    <dt className="text-xs text-gray-400">{f.label}</dt>
                    <dd className="text-sm font-medium text-gray-800">{SAMPLE_PATIENT_VALUES[f.key]}</dd>
                  </div>
                ))}
            </dl>
          </div>

          {/* Enabled sections, in order */}
          {sections.map((section, idx) => (
            <section
              key={section.key || idx}
              className={idx < sections.length - 1 ? "border-b border-gray-200 pb-3" : ""}
            >
              <h3 className="text-sm font-semibold text-primary-700">
                {numberedSectionTitle(section, idx)}
              </h3>
              {section.key === "treatment_recommendation" || section.key === "supportive_care" ? (
                <div className="mt-2 overflow-hidden rounded-lg border border-gray-200">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        {TABLE_COLUMNS.map((col) => (
                          <th
                            key={col.label}
                            className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider"
                          >
                            {col.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        {TABLE_COLUMNS.map((col) => (
                          <td key={col.label} className="px-4 py-3 align-top">
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${col.badge}`}
                            >
                              {col.label}
                            </span>
                            <p className="mt-1.5 text-xs italic text-gray-400">[auto-generated]</p>
                          </td>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="mt-1 text-xs italic text-gray-400">
                  [Content auto-generated from the clinical record at report time]
                </p>
              )}
            </section>
          ))}

          {/* Signature labels */}
          <div className="grid grid-cols-2 gap-8 pt-6 mt-4 border-t border-gray-200">
            <div>
              <div className="border-b border-gray-300 mb-2 pb-8" />
              <p className="text-xs text-gray-500">
                {branding.signatureLeftLabel || "Lead Oncologist Signature"}
              </p>
            </div>
            <div>
              <div className="border-b border-gray-300 mb-2 pb-8" />
              <p className="text-xs text-gray-500">
                {branding.signatureRightLabel || "MDT Coordinator Verification"}
              </p>
            </div>
          </div>

          {/* Footer disclaimer */}
          {branding.footerText && (
            <div className="border-t border-gray-200 pt-4">
              <p className="text-xs text-gray-400">{branding.footerText}</p>
            </div>
          )}

          {/* Clinical disclaimer */}
          <div className="border-t border-gray-200 pt-4">
            <p className="text-xs text-gray-500 italic">
              For clinical discussion only. Requires review and sign-off by the treating oncology team.
            </p>
          </div>
        </div>
      </div>

      <p className="text-xs text-gray-400 mt-3 text-center">
        Sample data — layout and branding only, not a real patient report.
      </p>
    </div>
  );
};

export default MdtReportPreview;